import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Keyword, KeywordAttributes } from "~/api/Api";
import FrequencySelect from "~/components/common/FrequencySelect";
import { useTeam } from "~/contexts/TeamProvider";

import { apiClient } from "../../api/ApiClient";
import FormLabel from "../login/formLabel/FormLabel";
import Select from "../mantine/select/Select";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Switch from "../switch/Switch";
import Input from "../ui/input/Input";
import Modal from "../ui/modal/Modal";
import styles from "./EditKeywordModal.module.scss";

type EditKeywordFormData = {
  refresh_frequency_override: number | null;
  location_id: number;
  country: KeywordAttributes["country"];
  language: KeywordAttributes["language"];
  ranking_url: string;
  ranking_url_lock: NonNullable<KeywordAttributes["ranking_url_lock"]>;
  isTrackLocal: boolean;
};

interface EditKeywordModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSuccess: () => void;
  keywordId: number;
  keywordName: string;
  siteId: number;
  initialData?: Partial<
    Pick<
      Keyword,
      | "name"
      | "country"
      | "language"
      | "ranking_url"
      | "ranking_url_lock"
      | "location_id"
      | "location_name"
    >
  > & { refresh_frequency_override?: number | null };
}

const EditKeywordModal = ({
  isOpen,
  closeModal,
  onSuccess,
  keywordId,
  siteId,
  initialData,
}: EditKeywordModalProps) => {
  const { team } = useTeam();

  const [locationOptions, setLocationOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const { control, handleSubmit, watch, setValue } =
    useForm<EditKeywordFormData>({
      values: {
        refresh_frequency_override:
          initialData?.refresh_frequency_override ?? null,
        location_id: initialData?.location_id || 0,
        country: initialData?.country || "google.com",
        language: initialData?.language || "en",
        isTrackLocal: !!initialData?.location_id,
        ranking_url: initialData?.ranking_url ?? "",
        ranking_url_lock: initialData?.ranking_url_lock ?? false,
      },
    });

  useEffect(() => {
    if (initialData?.location_id && initialData?.location_name) {
      setLocationOptions([
        {
          value: String(initialData.location_id),
          label: initialData.location_name,
        },
      ]);
    }
  }, [initialData]);

  const { mutate: getLocations } = useMutation({
    mutationFn: async (query: string) => {
      const response = await apiClient.locations.searchList({
        fulltext: query,
      });

      const locationOptions =
        response.data.locations?.map((location) => ({
          value: String(location.id),
          label: location.name || "",
        })) || [];
      setLocationOptions(locationOptions);

      return locationOptions;
    },
  });

  const { data: languages } = useQuery({
    queryKey: ["languagesList"],
    queryFn: async () => {
      const response = await apiClient.sites.languagesList();

      return response?.data.languages;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: regions } = useQuery({
    queryKey: ["regionsList"],
    queryFn: async () => {
      const response = await apiClient.sites.regionsList();

      return response?.data.regions;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { mutate: updateKeyword, isPending } = useMutation({
    mutationFn: async (data: EditKeywordFormData) => {
      const payload = {
        name: initialData?.name || "",
        refresh_frequency_override: data.refresh_frequency_override,
        ...(data.location_id && { location_id: Number(data.location_id) }),
        country: data.country,
        language: data.language,
        ranking_url: data.ranking_url,
        ranking_url_lock: data.ranking_url_lock,
      };

      // @ts-expect-error BE types
      return apiClient.sites.keywordsPartialUpdate(siteId, keywordId, payload);
    },
    onSuccess: () => {
      onSuccess();
      closeModal();
    },
  });

  const regionOptions = regions
    ? Object.entries(regions).map(([key, value]) => ({
        value: key,
        label: `${key} (${value.name})`,
      }))
    : [];

  const languageOptions = languages
    ? Object.entries(languages).map(([key, value]) => ({
        value: key,
        label: `${value} (${key})`,
      }))
    : [];

  return (
    <Modal
      isLoading={isPending}
      isOpen={isOpen}
      closeModal={closeModal}
      modalLabel={
        // @ts-expect-error TS2339 non-existing className
        <h3 className={styles.modalTitle}>
          Edit Keyword: {initialData?.name || ""}
        </h3>
      }
      buttonLabel="Save Changes"
      formId="editKeywordForm"
    >
      <div>
        <p>
          Here you can change the language, local targeting and specify a URL to
          track and optimize.{" "}
        </p>
        <form
          id="editKeywordForm"
          onSubmit={handleSubmit((data) => updateKeyword(data))}
        >
          <div className={styles.formGrid}>
            {watch("ranking_url_lock") && (
              <NotificationBlock
                message="Note that if you specify a URL, we will track ranking for this URL only. Only set if you want to track a specific page."
                type="warning"
              />
            )}

            <Controller
              name="ranking_url"
              control={control}
              render={({ field }) => (
                <Input
                  ownProps={{ noErrors: true }}
                  placeholder="Enter the URL that should rank for this keyword"
                  {...field}
                />
              )}
            />

            <div className={styles.formGridSwitch}>
              <Switch
                checked={watch("ranking_url_lock")}
                onChange={(e) =>
                  setValue("ranking_url_lock", e.currentTarget.checked, {
                    shouldDirty: true,
                  })
                }
              />
              <FormLabel label="Track this page URL only" />
            </div>

            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Select
                  label={<FormLabel label="Country" />}
                  data={regionOptions}
                  searchable
                  {...field}
                  value={field.value ? String(field.value) : ""}
                />
              )}
            />

            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Select
                  label={<FormLabel label="Language" />}
                  data={languageOptions}
                  searchable
                  {...field}
                  value={field.value ? String(field.value) : ""}
                />
              )}
            />

            <Controller
              name="refresh_frequency_override"
              control={control}
              render={({ field }) => (
                <FrequencySelect
                  label="Refresh Frequency"
                  defaultSeconds={team?.refresh_frequency}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <div className={styles.formGridSwitch}>
              <Switch
                checked={watch("isTrackLocal")}
                onChange={(e) =>
                  setValue("isTrackLocal", e.currentTarget.checked, {
                    shouldDirty: true,
                  })
                }
              />
              <FormLabel label="Track Local Results" />
            </div>

            {watch("isTrackLocal") && (
              <Controller
                name="location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    label={<FormLabel label="Location" />}
                    data={locationOptions}
                    searchable
                    clearable
                    {...field}
                    onSearchChange={(query) => {
                      getLocations(query);
                    }}
                    value={field.value ? String(field.value) : ""}
                    placeholder="Enter a location or zip code"
                    comboboxProps={{ position: "bottom" }}
                  />
                )}
              />
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditKeywordModal;
